import { useCallback, useState } from "react";
import config from "@/config";
import type {
  RetryExecuteResponse,
  RetryPreflightResponse,
  RetryReleaseBatchHoldResponse,
  RetryReleaseSuccessHoldResponse,
  RetryRequestBody,
} from "@/types/intake";

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${config.API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string") detail = data.detail;
    } catch {
      // body wasn't JSON; ignore
    }
    throw new Error(detail || `${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useRetryPreflight() {
  const [result, setResult] = useState<RetryPreflightResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (body: RetryRequestBody) => {
    setLoading(true);
    setError(null);
    try {
      const data = await postJson<RetryPreflightResponse>(
        "/api/dashboard/retry/preflight",
        body,
      );
      setResult(data);
      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "preflight failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, run, reset };
}

export function useRetryReleaseBatchHold() {
  const [result, setResult] = useState<RetryReleaseBatchHoldResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (caseIds: string[]) => {
    setLoading(true);
    setError(null);
    try {
      const data = await postJson<RetryReleaseBatchHoldResponse>(
        "/api/dashboard/retry/release-batch-hold",
        { case_ids: caseIds },
      );
      setResult(data);
      return data;
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "release batch hold failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, run, reset };
}

export function useRetryReleaseSuccessHold() {
  const [result, setResult] = useState<RetryReleaseSuccessHoldResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (caseIds: string[]) => {
    setLoading(true);
    setError(null);
    try {
      const data = await postJson<RetryReleaseSuccessHoldResponse>(
        "/api/dashboard/retry/release-success-hold",
        { case_ids: caseIds },
      );
      setResult(data);
      return data;
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "release success hold failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, run, reset };
}

export function useRetryExecute() {
  const [result, setResult] = useState<RetryExecuteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (body: RetryRequestBody) => {
    setLoading(true);
    setError(null);
    try {
      const data = await postJson<RetryExecuteResponse>(
        "/api/dashboard/retry/execute",
        body,
      );
      setResult(data);
      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "retry failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, run, reset };
}
